import React, { createContext, useContext, useState, useRef } from "react";
import { SnackbarType } from "../components/sharedComponents/Snackbar";
import { Toast, ToastMessageOptions } from "primereact/toast";

export type MessageText = string | JSX.Element;
export type Message = {
  text: MessageText;
  show: boolean;
  snackbarType: SnackbarType;
  index: number;
  dismissHandler?: () => void;
};

type ContextProps = {
  snackbarType?: SnackbarType;
  show?: boolean;
  text?: string;
  hideSnackbar?: () => void;
  messages?: Message[];
  removeSnackbarMessage?: (message: Message) => void;
};

export type showSnackbarType = (
  snackbarType: SnackbarType,
  txt: MessageText,
  dismissHandler?: () => void
) => void;
export type removeSnackbarMessageType = (message: Message) => void;

export type ContextActionProps = {
  showSnackbar?: showSnackbarType;
};

export const SnackbarPropContext = createContext<ContextProps>({});
export const SnackbarActionContext = createContext<ContextActionProps>({});

export const useSnackbarPropContext = () => useContext(SnackbarPropContext);
export const useSnackbarActionContext = () => useContext(SnackbarActionContext);

type Props = {
  children?: React.ReactNode;
};

const delay = async () => {
  // this is here to avoid any chance of multiple toasts clogging up the provider
  const sleep = (milliseconds: number) => {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  };
  await sleep(250);
};

const SnackbarProvider: React.FC<Props> = ({ ...props }) => {
  const toast = useRef<Toast>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const showSnackbar: showSnackbarType = (
    snackbarType: SnackbarType,
    txt: MessageText,
    dismissHandler?: () => void,
    timeout: number = 3000,
    messageSummary = ""
  ) => {
    delay();

    let toastSeverity: ToastMessageOptions["severity"] =
      snackbarType !== "" ? snackbarType : "success";
    toast.current?.show({
      severity: toastSeverity,
      summary: messageSummary,
      detail: txt,
      life: timeout,
    });
  };

  return (
    <SnackbarPropContext.Provider value={{ messages }}>
      <SnackbarActionContext.Provider value={{ showSnackbar }}>
        {props.children}
        <Toast baseZIndex={3000} ref={toast} position="top-center" />
      </SnackbarActionContext.Provider>
    </SnackbarPropContext.Provider>
  );
};

const showSnackbarMessage = (
  showSnackbar: showSnackbarType | undefined,
  msgType: SnackbarType,
  msg: MessageText,
  messageSummary = ""
) => {
  if (showSnackbar) {
    showSnackbar(msgType, msg);
  }
};

const attemptShowErrorsInSnackbar = (
  showSnackbar: showSnackbarType | undefined,
  error: any
) => {
  if (error?.response?.data?.errors instanceof Array) {
    return error.response.data.errors.map((error: any) => {
      showSnackbarMessage(showSnackbar, SnackbarType.Error, error.message);
    });
  }

  if (error?.response?.data?.errors) {
    const values = Object.values(error?.response?.data?.errors);
    const errors = [];
    values.forEach((errorMessage: Array<string>) =>
      errorMessage.map((error) => errors.push(error))
    );

    return errors.map((error: any) => {
      showSnackbarMessage(showSnackbar, SnackbarType.Error, error);
    });
  }

  if (error?.response?.data?.message) {
    return showSnackbarMessage(
      showSnackbar,
      SnackbarType.Error,
      error.response.data.message
    );
  }

  if (error?.graphQLErrors.length > 0) {
    return error.graphQLErrors.forEach((error: any) => {
      showSnackbarMessage(showSnackbar, SnackbarType.Error, error.message);
    });
  }

  return error?.networkError.result.errors.forEach((error: any) => {
    showSnackbarMessage(showSnackbar, SnackbarType.Error, error.message);
  });
};

export { SnackbarProvider, showSnackbarMessage, attemptShowErrorsInSnackbar };
